/**
 * Dashboard Cache Utilities
 * Provides caching functionality for progressive loading
 */

interface CacheConfig {
  // Cache expiry in milliseconds (default: 5 minutes)
  expiryMs?: number
  // Storage type (default: localStorage)
  storage?: 'localStorage' | 'sessionStorage'
}

interface CachedData<T> {
  data: T
  timestamp: number
  version: string
}

const DEFAULT_EXPIRY_MS = 5 * 60 * 1000 // 5 minutes
const CACHE_VERSION = '1.0.0'
const CACHE_KEY_PREFIX = 'vt_dashboard_'

export class DashboardCache {
  private expiryMs: number
  private storage: Storage

  constructor(config: CacheConfig = {}) {
    this.expiryMs = config.expiryMs || DEFAULT_EXPIRY_MS
    this.storage = config.storage === 'sessionStorage' ? sessionStorage : localStorage
  }

  /**
   * Get cached data for a specific key
   */
  get<T>(key: string): T | null {
    try {
      const cacheKey = `${CACHE_KEY_PREFIX}${key}`
      const cached = this.storage.getItem(cacheKey)

      if (!cached) return null

      const parsedCache: CachedData<T> = JSON.parse(cached)

      // Check version
      if (parsedCache.version !== CACHE_VERSION) {
        this.remove(key)
        return null
      }

      // Check expiry
      const now = Date.now()
      if (now - parsedCache.timestamp > this.expiryMs) {
        this.remove(key)
        return null
      }

      return parsedCache.data
    } catch (error) {
      return null
    }
  }

  /**
   * Set cached data for a specific key
   */
  set<T>(key: string, data: T): void {
    try {
      const cacheKey = `${CACHE_KEY_PREFIX}${key}`
      const cacheData: CachedData<T> = {
        data,
        timestamp: Date.now(),
        version: CACHE_VERSION,
      }

      this.storage.setItem(cacheKey, JSON.stringify(cacheData))
    } catch (error) {
      // If storage is full, try to clear old cache entries
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        this.clearOldEntries()
      }
    }
  }

  /**
   * Remove cached data for a specific key
   */
  remove(key: string): void {
    const cacheKey = `${CACHE_KEY_PREFIX}${key}`
    this.storage.removeItem(cacheKey)
  }

  /**
   * Clear all dashboard cache entries
   */
  clearAll(): void {
    const keys = Object.keys(this.storage)
    keys.forEach((key) => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        this.storage.removeItem(key)
      }
    })
  }

  /**
   * Clear old cache entries to free up space
   */
  private clearOldEntries(): void {
    const keys = Object.keys(this.storage)
    const cacheEntries: Array<{ key: string; timestamp: number }> = []

    // Collect all dashboard cache entries with timestamps
    keys.forEach((key) => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        try {
          const cached = this.storage.getItem(key)
          if (cached) {
            const parsedCache = JSON.parse(cached)
            cacheEntries.push({ key, timestamp: parsedCache.timestamp || 0 })
          }
        } catch {
          // Remove invalid entries
          this.storage.removeItem(key)
        }
      }
    })

    // Sort by timestamp (oldest first) and remove the oldest 25%
    cacheEntries.sort((a, b) => a.timestamp - b.timestamp)
    const entriesToRemove = Math.ceil(cacheEntries.length * 0.25)

    for (let i = 0; i < entriesToRemove; i++) {
      this.storage.removeItem(cacheEntries[i].key)
    }
  }

  /**
   * Check if cached data exists and is valid
   */
  has(key: string): boolean {
    return this.get(key) !== null
  }

  /**
   * Get age of cached data in milliseconds
   */
  getAge(key: string): number | null {
    try {
      const cacheKey = `${CACHE_KEY_PREFIX}${key}`
      const cached = this.storage.getItem(cacheKey)

      if (!cached) return null

      const parsedCache: CachedData<any> = JSON.parse(cached)
      return Date.now() - parsedCache.timestamp
    } catch {
      return null
    }
  }
}

// Create singleton instance
export const dashboardCache = new DashboardCache()

// Cache key generators
export const CACHE_KEYS = {
  userData: (userId: string) => `user_${userId}`,
  companyData: () => 'company',
  teamLeaves: (userId: string) => `team_leaves_${userId}`,
  companyLeaves: () => 'company_leaves',
  locations: () => 'locations',
  visibilityAddon: () => 'visibility_addon',
} as const
